import { useEffect, useMemo, useState } from 'react';

import { Divider, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { PageTitle } from '~/components/common';
import {
  DeliveryAgencyExcelDownloadModal,
  DeliveryAgencyReportFilter,
  DeliveryAgencyReportSummary,
  DeliveryAgencyReportTable,
  DeliveryAgencyReportUpdateInfo,
  type TypeDeliveryAgencyReportSummarySubValueKey,
  type TypeDeliveryAgencyReportSummaryValueKey,
} from '~/components/delivery-agency';
import { PageWrapper } from '~/components/layout';

import { useModal, useStableCallback, useToggle } from '~/hooks/common';
import {
  deliveryAgencyKeys,
  useGetDeliveryResultReportsQuery,
  useGetDeliveryResultReportsSearchConditionsQuery,
  usePostDeliveryResultReportsDownloadMutation,
} from '~/hooks/delivery-agency/queries';
import { useUserInfoStore } from '~/store';
import { addIf, renderFilterErrorMessage } from '~/utils/common';
import { formatDate, getDateByOffset } from '~/utils/date';

import {
  BREAD_CRUMB,
  DELIVERY_AGENCY_DELIVERY_TYPE,
  DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS,
  DELIVERY_AGENCY_REPORT_IN_PROGRESS_STATUS_CODES,
  INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
  INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  MAX_DELIVERY_AGENCY_DATE_RANGE,
  OPTION_ALL,
  PATH,
  TOAST_MESSAGE,
} from '~/constants';

import type {
  DeliveryAgencyReportFilterOptions,
  DeliveryAgencyReportFilterValues,
  DeliveryAgencyReportFilterValuesStatus,
  DeliveryResultReportsDownloadInput,
  GetDeliveryResultReportsParams,
} from '~/types/delivery-agency.types';

const EMPTY_FILTER_OPTIONS: DeliveryAgencyReportFilterOptions = {
  searchKeywordTypes: [],
  shippingLabelStatuses: [],
  deliveryFailureReasonCategories: [],
  deliveryTypes: [],
  deliveryResults: [],
};

/** 서머리 선택 상태 (박스 멀티 선택 + 배송완료 서브 칩 멀티 토글) */
type TypeSummarySelection = {
  valueKeys: TypeDeliveryAgencyReportSummaryValueKey[];
  subValueKeys: TypeDeliveryAgencyReportSummarySubValueKey[];
};

const getInitialSearchFilterValues = (): DeliveryAgencyReportFilterValues => ({
  keywordType: null,
  keyword: '',
  deliveryDateRange: { start: getDateByOffset(0, 'day'), end: getDateByOffset(0, 'day') },
  shippingLabelStatuses: [OPTION_ALL],
  deliveryFailureReasonCategories: [OPTION_ALL],
  deliveryType: OPTION_ALL,
  deliveryResult: OPTION_ALL,
  pagination: {
    currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
    totalCount: 0,
    pageSize: INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  },
});

/**
 * [배송 관리 > 배송 결과 리포트] 페이지 컴포넌트
 */
const DeliveryAgencyReportPage = () => {
  const queryClient = useQueryClient();

  const hasPermission = useUserInfoStore((state) => state.hasPermission);

  const hasFulfillmentPermission = hasPermission([
    { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] },
    { ownerType: 'NFA', logisticsTypes: ['FULFILLMENT'] },
  ]);

  const { data: searchFilterOptions, isError: isSearchConditionsError } =
    useGetDeliveryResultReportsSearchConditionsQuery({ silent: true });

  const filterOptions = useMemo<DeliveryAgencyReportFilterOptions>(() => {
    const baseOptions = searchFilterOptions ?? EMPTY_FILTER_OPTIONS;

    if (hasFulfillmentPermission) return baseOptions;

    return {
      ...baseOptions,
      deliveryTypes: baseOptions.deliveryTypes.filter((option) => option.value !== DELIVERY_AGENCY_DELIVERY_TYPE.DAY),
    };
  }, [searchFilterOptions, hasFulfillmentPermission]);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const [searchFilterValues, setSearchFilterValues] =
    useState<DeliveryAgencyReportFilterValues>(getInitialSearchFilterValues);
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<DeliveryAgencyReportFilterValuesStatus>({
    keyword: 'default',
    deliveryDateRange: 'default',
  });

  const [reportsQueryParams, setReportsQueryParams] = useState<GetDeliveryResultReportsParams | null>(null);

  const [summarySelection, setSummarySelection] = useState<TypeSummarySelection>({
    valueKeys: [],
    subValueKeys: [],
  });

  const reportsQuery = useGetDeliveryResultReportsQuery({ params: reportsQueryParams, showGlobalDimOverlay: false });

  const reportsDownloadMutation = usePostDeliveryResultReportsDownloadMutation();

  const excelDownloadModal = useModal();

  useEffect(() => {
    if (searchFilterOptions) {
      setSearchFilterValues((prev) => ({
        ...prev,
        keywordType: prev.keywordType ?? searchFilterOptions.searchKeywordTypes[0] ?? null,
      }));
    }
  }, [searchFilterOptions]);

  useEffect(() => {
    if (isSearchConditionsError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [isSearchConditionsError]);

  const syncReportsPagination = useStableCallback(() => {
    if (!reportsQuery.data || reportsQuery.isPlaceholderData) return;

    const { pagination } = reportsQuery.data;
    setSearchFilterValues((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        totalCount: pagination.listTotal,
        pageSize: pagination.listView,
        currentPage: pagination.cursor,
      },
    }));
  });

  useEffect(() => {
    syncReportsPagination();
  }, [reportsQuery.dataUpdatedAt, reportsQuery.isPlaceholderData, syncReportsPagination]);

  const handleSearch = (
    filterValues: DeliveryAgencyReportFilterValues = searchFilterValues,
    selection: TypeSummarySelection = summarySelection
  ) => {
    const { start, end } = filterValues.deliveryDateRange ?? {};
    const isDateRangeEmpty = !start || !end;
    const isDateLimitExceeded = dayjs(end).diff(start, 'day') >= MAX_DELIVERY_AGENCY_DATE_RANGE;

    if (isDateRangeEmpty || isDateLimitExceeded) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, deliveryDateRange: 'error' }));
      dialogService.addDialog({
        children: `배송예정일은 최대 ${MAX_DELIVERY_AGENCY_DATE_RANGE}일 기간으로 입력 후 검색해주세요.`,
      });
      return;
    }

    // 진행 중(IN_PROGRESS) 카드는 합성 키이므로 실제 상태 코드 2개(출고 대기/진행중, 배송 중)로 확장한다.
    const summaryStatuses = selection.valueKeys
      .filter((key) => key !== 'totalCount')
      .flatMap<string>((key) => (key === 'IN_PROGRESS' ? [...DELIVERY_AGENCY_REPORT_IN_PROGRESS_STATUS_CODES] : [key]));

    const completedStatus = DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED;
    if (selection.subValueKeys.length > 0 && !summaryStatuses.includes(completedStatus)) {
      summaryStatuses.push(completedStatus);
    }

    const params: GetDeliveryResultReportsParams = {
      estimatedDeliveryDateFrom: formatDate(filterValues.deliveryDateRange?.start, 'YYYY-MM-DD') ?? '',
      estimatedDeliveryDateTo: formatDate(filterValues.deliveryDateRange?.end, 'YYYY-MM-DD') ?? '',
      searchKeywordType: filterValues.keywordType?.value ?? '',
      searchKeyword: filterValues.keyword.trim(),
      ...addIf(
        filterValues.shippingLabelStatuses.some((option) => option.value !== OPTION_ALL.value),
        'reportedShippingLabelStatuses',
        filterValues.shippingLabelStatuses
          .filter((option) => option.value !== OPTION_ALL.value)
          .map((option) => option.value)
          .join(',')
      ),
      ...addIf(
        filterValues.deliveryFailureReasonCategories.some((option) => option.value !== OPTION_ALL.value),
        'deliveryFailureReasonCategories',
        filterValues.deliveryFailureReasonCategories
          .filter((option) => option.value !== OPTION_ALL.value)
          .map((option) => option.value)
          .join(',')
      ),
      ...addIf(filterValues.deliveryType?.value, 'deliveryType'),
      ...addIf(filterValues.deliveryResult?.value, 'deliveryResult'),
      ...addIf(summaryStatuses.length > 0, 'summaryShippingLabelStatuses', summaryStatuses.join(',')),
      ...addIf(selection.subValueKeys.length > 0, 'summaryDeliveryResults', selection.subValueKeys.join(',')),
      page: filterValues.pagination.currentPage,
      size: filterValues.pagination.pageSize,
    };

    setSearchFilterValues(filterValues);
    setSummarySelection(
      selection.valueKeys.length === 0 && selection.subValueKeys.length === 0
        ? { valueKeys: ['totalCount'], subValueKeys: [] }
        : selection
    );

    queryClient.invalidateQueries({
      queryKey: deliveryAgencyKeys.deliveryResultReports(params),
    });
    setReportsQueryParams(params);
  };

  const handleSelectSummary = (valueKey: TypeDeliveryAgencyReportSummaryValueKey) => {
    if (!reportsQueryParams) return;

    const completedStatus = DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED;

    const nextSelection: TypeSummarySelection =
      valueKey === 'totalCount'
        ? { valueKeys: ['totalCount'], subValueKeys: [] }
        : {
            valueKeys: summarySelection.valueKeys.includes(valueKey)
              ? summarySelection.valueKeys.filter((key) => key !== valueKey)
              : [...summarySelection.valueKeys.filter((key) => key !== 'totalCount'), valueKey],
            subValueKeys: valueKey === completedStatus ? [] : summarySelection.subValueKeys,
          };

    handleSearch(
      {
        ...searchFilterValues,
        pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR },
      },
      nextSelection
    );
  };

  const handleToggleSummarySub = (subValueKey: TypeDeliveryAgencyReportSummarySubValueKey) => {
    if (!reportsQueryParams) return;

    const completedStatus = DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED;

    const subValueKeys = summarySelection.subValueKeys.includes(subValueKey)
      ? summarySelection.subValueKeys.filter((key) => key !== subValueKey)
      : [...summarySelection.subValueKeys, subValueKey];

    const baseValueKeys = summarySelection.valueKeys.filter((key) => key !== 'totalCount' && key !== completedStatus);

    handleSearch(
      {
        ...searchFilterValues,
        pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR },
      },
      {
        valueKeys: subValueKeys.length > 0 ? baseValueKeys : [...baseValueKeys, completedStatus],
        subValueKeys,
      }
    );
  };

  const handleConfirmExcelDownload = (reason: string) => {
    if (!reportsQueryParams) return;

    const {
      page: _page,
      size: _size,
      reportedShippingLabelStatuses,
      deliveryFailureReasonCategories,
      summaryShippingLabelStatuses,
      summaryDeliveryResults,
      ...rest
    } = reportsQueryParams;

    const input: DeliveryResultReportsDownloadInput = {
      ...rest,
      ...addIf(
        reportedShippingLabelStatuses,
        'reportedShippingLabelStatuses',
        reportedShippingLabelStatuses?.split(',')
      ),
      ...addIf(
        deliveryFailureReasonCategories,
        'deliveryFailureReasonCategories',
        deliveryFailureReasonCategories?.split(',')
      ),
      ...addIf(summaryShippingLabelStatuses, 'summaryShippingLabelStatuses', summaryShippingLabelStatuses?.split(',')),
      ...addIf(summaryDeliveryResults, 'summaryDeliveryResults', summaryDeliveryResults?.split(',')),
    };

    reportsDownloadMutation.mutate(
      { downloadReason: reason, input },
      {
        onSuccess: () => {
          excelDownloadModal.handleConfirmModal();
          toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
        },
      }
    );
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
        pageSize: value.value,
      },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: value },
    });
  };

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...getInitialSearchFilterValues(),
      pagination: searchFilterValues.pagination,
      keywordType: searchFilterOptions?.searchKeywordTypes[0] ?? null,
    });
    setSearchFilterValuesStatus({
      keyword: 'default',
      deliveryDateRange: 'default',
    });
  };

  const totalCount = reportsQuery.data?.pagination.listTotal ?? 0;

  return (
    <PageWrapper>
      <PageTitle
        title="배송 결과 리포트"
        breadcrumbs={BREAD_CRUMB[PATH.DELIVERY_AGENCY_REPORT]}
        handleFilterToggle={handleFilterToggle}
        filterExtra={<DeliveryAgencyReportUpdateInfo />}
      />
      {isFilterOpen && (
        <DeliveryAgencyReportFilter
          options={filterOptions}
          values={searchFilterValues}
          valuesStatus={searchFilterValuesStatus}
          onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
          onChangeStatus={(status) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }))}
          onSearch={() =>
            handleSearch(
              {
                ...searchFilterValues,
                pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR },
              },
              { valueKeys: ['totalCount'], subValueKeys: [] }
            )
          }
          onReset={handleResetFilterValues}
        />
      )}
      <Divider color="gray100" />
      <DeliveryAgencyReportSummary
        summary={reportsQuery.data?.data.summary ?? null}
        selectedValueKeys={summarySelection.valueKeys}
        selectedSubValueKeys={summarySelection.subValueKeys}
        onSelect={handleSelectSummary}
        onToggleSub={handleToggleSummarySub}
      />
      <DeliveryAgencyReportTable
        data={reportsQuery.data?.data.items ?? null}
        pagination={{ ...searchFilterValues.pagination, totalCount }}
        isDownloadReady={reportsQueryParams !== null && totalCount > 0}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onClickExcelDownload={excelDownloadModal.handleOpenModal}
      />
      {excelDownloadModal.isOpen && (
        <DeliveryAgencyExcelDownloadModal
          title="리포트 전체 다운로드"
          open={excelDownloadModal.isOpen}
          onCancel={excelDownloadModal.handleCloseModal}
          onConfirm={(reason) => handleConfirmExcelDownload(reason)}
        />
      )}
    </PageWrapper>
  );
};

export default DeliveryAgencyReportPage;
